/**
 * Preview Panel
 *
 * Right panel of the Home Layout Manager showing visual preview of:
 * - Mobile Home: Phone frame mockup with banners, carousel, categories
 * - Web Home: Scaled desktop layout
 * - Shop Page: Category grid view
 *
 * Supports store filtering and compare mode (draft vs live side-by-side).
 *
 * @module HomeLayoutManager/components/PreviewPanel
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Chip,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Modal,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Image as ImageIcon,
  UnfoldMore as ExpandIcon,
  UnfoldLess as CollapseIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  NewReleases as NewIcon,
  TrendingUp as PopularIcon,
  Warning as RestrictedIcon,
  LocalOffer as TagIcon,
  Inventory2 as InventoryIcon,
  FavoriteBorder as YouMightLikeIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import {
  APP,
  APP_GUTTER,
  APP_SCREEN_WIDTH,
  AppBannerDots,
  AppBannerFrame,
  AppBrandTile,
  AppCategoryTile,
  AppDeliverToBar,
  AppHeaderBar,
  AppProductCard,
  AppRow,
  AppSearchField,
  AppSectionHeader,
  AppStatusBar,
  AppTabBar,
  SECTION_MARGIN_BOTTOM,
  useAppSkinAssets,
} from './AppSkin';
import { useHomeLayout } from '../HomeLayoutContext';
import type { ConfigTab } from './ConfigurationPanel';
import { httpClient } from '../../../components/fetchApiData';
import config from '../../../config';
import Logger from '../../../utils/Logger';
import {
  fetchStores as fetchStoresFromApi,
  fetchPreviewProducts,
  fetchPreviewProductsBatch,
  fetchCategoryProducts,
  fetchBrands as fetchBrandsFromApi,
} from '../api';
import { useStoreVisibility } from '../../../hooks/useStoreVisibility';
import type { Brand } from '../api';
import type {
  ViewMode,
  BannerPositionDraft,
  CategoryDraft,
  CarouselVerticalConfig,
  Store,
} from '../types';

/**
 * Product attachment structure
 */
interface ProductAttachment {
  id?: string;
  url?: string;
  thumbnailUrl?: string;
  downloadUrl?: string;
  type?: string;
}

/**
 * Related product structure from API
 * Returned by GET /products/:id in relatedProducts array
 */
interface RelatedProduct {
  id: string;
  position: number;
  related: {
    id: string;
    name: string;
    priceTier1?: number;
    stock: number;
    attachments?: ProductAttachment[];
  };
}

/**
 * Product data structure from the API
 * Matches the actual backend response structure with full metadata
 */
interface Product {
  id: string;
  name: string;
  price: number;
  priceTier1?: number;
  stock: number;
  sku?: string;
  barcode?: string;
  description?: string;
  brand?: string | { name: string };
  tags?: Array<string | { name: string }>;
  status?: string;
  isNew?: boolean;
  isPopular?: boolean;
  isRestricted?: boolean;
  isSellable?: boolean;
  isSellableInApp?: boolean;
  legacyExternalId?: string;
  lastSyncedAt?: string;
  categoryRelation?: {
    name: string;
    respectsEarlyCutoff?: boolean;
  } | string;
  attachments?: ProductAttachment[];
  /** "You Might Like" related products */
  relatedProducts?: RelatedProduct[];
}

/**
 * Helper to get brand name from product (handles both string and object formats)
 */
function getBrandName(product: Product): string | undefined {
  if (!product.brand) return undefined;
  if (typeof product.brand === 'string') return product.brand;
  return product.brand.name;
}

/**
 * Helper to get tag name (handles both string and object formats)
 */
function getTagName(tag: string | { name: string }): string {
  if (typeof tag === 'string') return tag;
  return tag.name;
}

/**
 * Helper to get category name from product (handles various formats)
 */
function getCategoryName(product: Product, fallback: string): string {
  if (!product.categoryRelation) return fallback;
  if (typeof product.categoryRelation === 'string') return product.categoryRelation;
  return product.categoryRelation.name || fallback;
}

const API_BASE = `${config.API_URL}/admin`;

// ============================================================================
// Phone frame sizing
// ============================================================================

/** Layout width of the phone mock — a real iPhone's CSS width, and the width
 *  every measurement in AppSkin is taken at, so 1px here == 1pt in the app. */
const PHONE_WIDTH = APP_SCREEN_WIDTH;
/** Screen height of an iPhone X-class device, which is what the chassis draws. */
const PHONE_HEIGHT = 812;
/** Bezel thickness around the screen. */
const PHONE_BEZEL = 11;
/** Corner radius of the screen itself; the chassis adds the bezel on top. */
const PHONE_SCREEN_RADIUS = 34;
/** Outer width of the device (screen + bezel both sides + the 1px chassis edge),
 *  which is what the preview column has to fit. */
const FRAME_WIDTH = PHONE_WIDTH + PHONE_BEZEL * 2 + 2;
/** Below this the frame stops shrinking and the column scrolls sideways instead. */
const PHONE_MIN_SCALE = 0.55;
/**
 * Never scale ABOVE 1:1.
 *
 * This used to allow 1.6x, so on a wide preview column the mock rendered at 600x1120 —
 * a phone blown up 60%, which is why it stopped reading as a phone. The point of the
 * preview is to show what the app looks like at the size it actually is, so a wide
 * column now just leaves whitespace around it rather than inflating it.
 */
const PHONE_MAX_SCALE = 1;

/**
 * Phone chassis — graphite, the way a device mock is normally drawn.
 *
 * It was near-black once, then went warm beige on the theory that a dark border
 * fought the red header. It didn't read as a phone at all after that: a pale
 * chassis on a pale admin background has no silhouette, and the "notch" was a
 * lighter rectangle sitting on top of the header rather than cut into it. Dark
 * bezel + a real pill cutout + a status bar is what makes the mock legible as a
 * screenshot of a phone, which is the entire point of the panel.
 */
const PHONE_CHASSIS = 'linear-gradient(160deg, #3A3A3C 0%, #1C1C1E 40%, #2C2C2E 100%)';
const PHONE_CHASSIS_EDGE = 'rgba(255, 255, 255, 0.16)';

/** Brand logos the home row shows before it scrolls. */
const BRANDS_VISIBLE = 8;

/**
 * Editor-only outline colour for the block the open section edits.
 *
 * Blue, not brand red: red is the app's own colour and appears all over the
 * preview (header, prices, "View All"), so a red outline read as part of the
 * screen rather than as an editor affordance.
 */
const HIGHLIGHT_COLOR = APP.editorAccent;

// ============================================================================
// Section ↔ preview mapping
// ============================================================================

/**
 * Which part of the preview each Content Control section is about.
 *
 * Used for two things: pointing the preview at the right screen (Shop lives on
 * its own tab, everything else is the home screen), and outlining the block
 * being edited so it's obvious which of the six sections you're looking at.
 */
const SECTION_ANCHORS: Partial<Record<ConfigTab, string>> = {
  home: 'scrollers',
  carousel: 'carousel',
  banners: 'banners',
  brands: 'brands',
};

/**
 * Preview data returned from the backend when filtering by store
 */
interface PreviewData {
  banners: BannerPositionDraft[];
  categories: CategoryDraft[];
  storeInfo: { id: string; name: string } | null;
}

/**
 * Fetch preview data filtered by store
 */
async function fetchPreviewData(storeId: string | null, view: ViewMode): Promise<PreviewData | null> {
  try {
    const params = new URLSearchParams();
    if (storeId) params.append('storeId', storeId);
    params.append('view', view);

    const url = `${API_BASE}/home-layout/preview?${params.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });
    return json as PreviewData;
  } catch (err) {
    Logger.error('Failed to fetch preview data:', err);
    return null;
  }
}

/**
 * Product card for preview - shows actual product image and name
 */
interface ProductCardProps {
  product?: Product;
  isLoading?: boolean;
}

/**
 * The app's ProductItem, fed from the admin's product shape.
 *
 * Image preference mirrors the app's own fallback chain; the admin API doesn't
 * return `gridImageUrl`, so it starts one link further down at `thumbnailUrl`.
 */
function ProductCard({ product, isLoading }: ProductCardProps): React.ReactElement {
  if (isLoading || !product) {
    return <AppProductCard loading />;
  }

  return (
    <AppProductCard
      product={{
        name: product.name,
        price: product.priceTier1 ?? product.price,
        imageUrl:
          product.attachments?.[0]?.thumbnailUrl ||
          product.attachments?.[0]?.downloadUrl ||
          product.attachments?.[0]?.url,
      }}
    />
  );
}

/**
 * Compare-mode diff tint.
 *
 * A 3px inset rule and a wash, rather than the old 4px MUI border plus inset
 * padding: that shifted the whole row off the app's 16pt gutter, so a diff view
 * no longer showed the layout you were diffing.
 */
function changeTint(changeType?: 'added' | 'modified' | 'removed'): Record<string, string> {
  if (!changeType) return {};
  const rule = changeType === 'added' ? '#2E7D32' : changeType === 'modified' ? '#ED6C02' : '#D32F2F';
  const wash =
    changeType === 'added'
      ? 'rgba(46, 125, 50, 0.06)'
      : changeType === 'modified'
        ? 'rgba(237, 108, 2, 0.06)'
        : 'rgba(211, 47, 47, 0.06)';
  return { boxShadow: `inset 3px 0 0 ${rule}`, backgroundColor: wash };
}

/**
 * Marker for a section that has nothing at the selected store, so the app will
 * drop it. Was a dashed orange box around the row; now it's an icon in the
 * header plus a fade on the row itself, which says the same thing without
 * redrawing the section as an admin widget.
 */
function EmptyAtStoreMark(): React.ReactElement {
  return (
    <Tooltip title="No products at the selected store — this section is hidden in the app">
      <RestrictedIcon sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
    </Tooltip>
  );
}

/** Placeholder where a row would be, set in the app's own type. */
function EmptyRowNote({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Box sx={{ py: 3, fontFamily: APP.font, fontSize: 13, fontStyle: 'italic', color: APP.grey }}>
      {children}
    </Box>
  );
}

/**
 * Category scroller that fetches and displays actual products
 */
interface CategoryScrollerProps {
  category: CategoryDraft;
  position: number;
  storeId: string | null;
  onViewAll?: (categoryName: string) => void;
  /** Number of products to show. 6 is roughly two screens of the app's 159pt cards. */
  productLimit?: number;
  /** Change type for compare mode highlighting */
  changeType?: 'added' | 'modified' | 'removed';
}

function CategoryScroller({ category, position, storeId, onViewAll, productLimit = 6, changeType }: CategoryScrollerProps): React.ReactElement {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if this category has no products at selected store
  const hasNoProducts = category.hasProductsAtStore === false;

  // Lazy-load: only fetch when scroller is near viewport (200px margin)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let mounted = true;
    setLoading(true);
    setFetchError(false);

    // Pass full category object with filter settings (cached + deduplicated via api.ts)
    fetchPreviewProducts(
      { name: category.name, homeFilterType: category.homeFilterType, homeFilterValue: category.homeFilterValue },
      storeId,
      productLimit
    )
      .then((data) => {
        if (mounted) {
          setProducts(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setFetchError(true);
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [isVisible, category.name, category.homeFilterType, category.homeFilterValue, storeId, productLimit]);

  if (fetchError) {
    return (
      <Box
        ref={containerRef}
        sx={{ mx: `${APP_GUTTER}px`, mb: `${SECTION_MARGIN_BOTTOM}px`, p: 1, bgcolor: 'error.light', borderRadius: 1 }}
      >
        <Typography variant="caption" sx={{ color: 'error.dark', fontSize: 12 }}>
          Failed to load "{category.name}" products
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ mb: `${SECTION_MARGIN_BOTTOM}px`, ...changeTint(changeType) }}>
      <AppSectionHeader
        title={category.name}
        order={position}
        onViewAll={() => onViewAll?.(category.name)}
        adornment={hasNoProducts ? <EmptyAtStoreMark /> : undefined}
      />
      <AppRow style={hasNoProducts ? { opacity: 0.45 } : undefined}>
        {loading ? (
          [1, 2, 3, 4].map((i) => <ProductCard key={i} isLoading />)
        ) : products.length > 0 ? (
          products.map((product) => <ProductCard key={product.id} product={product} />)
        ) : (
          <EmptyRowNote>No products in this category</EmptyRowNote>
        )}
      </AppRow>
    </Box>
  );
}

/**
 * Mock banner display component with optional auto-slide animation
 */
interface BannerPreviewProps {
  /** Banner position data */
  position: BannerPositionDraft | undefined;
  /** Banner type for styling */
  type: 'top' | 'slider' | 'bottom';
}

/**
 * Height of each banner slot, as a fraction of its width.
 *
 * These mirror the app one-for-one, so the preview crops exactly as much as the
 * phone does and no more:
 *   HomePageBanner        width * 0.25  (top)    / width * 0.5 (bottom, `isBig`)
 *   HomePageBannerSwiper  width * 0.41  (slider)
 *
 * The preview used to pin these at 50px (top and bottom) and 90px (slider). At the
 * ~343px preview width that showed under a third of the bottom banner — the artwork
 * is authored to these same ratios (1328x324, 1328x536, 1328x652), so a fixed height
 * cropped it with `object-fit: cover` and the bottom banner read as cut off.
 */
const BANNER_HEIGHT_RATIO: Record<BannerPreviewProps['type'], number> = {
  top: 0.25,
  slider: 0.41,
  bottom: 0.5,
};

function BannerPreview({ position, type }: BannerPreviewProps): React.ReactElement | null {
  const [currentSlide, setCurrentSlide] = useState(0);

  const activeItems = useMemo(
    () => position?.items.filter((item) => item.isActive) || [],
    [position?.items]
  );

  // Auto-slide for slider banners
  useEffect(() => {
    if (type !== 'slider' || activeItems.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % activeItems.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [type, activeItems.length]);

  if (!position || activeItems.length === 0) return null;

  const isSlider = type === 'slider';

  return (
    <>
      {activeItems.length > 0 && (
        <AppBannerFrame ratio={BANNER_HEIGHT_RATIO[type]}>
          {/* Sliding container */}
          <Box
            sx={{
              display: 'flex',
              width: `${activeItems.length * 100}%`,
              height: '100%',
              transition: 'transform 0.5s ease-in-out',
              transform: `translateX(-${(currentSlide * 100) / activeItems.length}%)`,
            }}
          >
            {activeItems.map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  width: `${100 / activeItems.length}%`,
                  height: '100%',
                  flexShrink: 0,
                }}
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.300',
                    }}
                  >
                    <ImageIcon color="disabled" fontSize="small" />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </AppBannerFrame>
      )}
      {/* The app puts the pagination dots BELOW the carousel (HomePageBannerSwiper's
          `pagination` style), not floating over the artwork. */}
      {isSlider && activeItems.length > 1 && (
        <AppBannerDots count={activeItems.length} active={currentSlide} onSelect={setCurrentSlide} />
      )}
    </>
  );
}

/**
 * Category carousel preview (icon row) - SQUARE icons matching mobile app
 * Mobile app shows 4 square icons per row
 */
interface CategoryCarouselPreviewProps {
  /** Categories to show in carousel */
  categories: CategoryDraft[];
  /** Whether this is web carousel */
  isWeb?: boolean;
}

function CategoryCarouselPreview({
  categories,
  isWeb = false,
}: CategoryCarouselPreviewProps): React.ReactElement {
  // Filter and sort by carouselPosition (independent carousel order)
  const carouselCategories = categories
    .filter((cat) => cat.isActive && (isWeb ? cat.showInCarouselWeb : cat.showInCarousel))
    .sort((a, b) => a.carouselPosition - b.carouselPosition);

  if (carouselCategories.length === 0) {
    return <EmptyRowNote>No carousel items</EmptyRowNote>;
  }

  /*
   * CategorySlide: a 78pt red tile with the category's own SVG at 60pt inside it.
   *
   * The tiles used to be 72pt with the SVG stretched edge-to-edge under
   * `object-fit: cover`, which cropped every icon, and the row carried a dashed
   * "viewport boundary" rule down its right-hand side. The phone frame IS the
   * viewport now — the row clips at the bezel exactly as the app clips at the
   * screen edge — so the rule was drawing a second, fictional one.
   */
  return (
    <AppRow>
      {carouselCategories.map((cat) => {
        const hasNoProducts = cat.hasProductsAtStore === false;
        return (
          <Tooltip
            key={cat.id}
            title={hasNoProducts ? 'No products at the selected store — hidden in the app' : ''}
            placement="top"
          >
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <AppCategoryTile name={cat.name} imageUrl={cat.imageUrl} dimmed={hasNoProducts} />
            </Box>
          </Tooltip>
        );
      })}
    </AppRow>
  );
}

/**
 * Fetch single product with "You Might Like" related products
 */
async function fetchProductWithRelated(productId: string, storeId: string | null): Promise<Product | null> {
  try {
    const params = new URLSearchParams();
    if (storeId) params.append('storeId', storeId);

    const url = `${config.API_URL}/products/${productId}${params.toString() ? `?${params.toString()}` : ''}`;
    const { json } = await httpClient(url, { method: 'GET' });
    return json as Product;
  } catch (err) {
    Logger.error(`Failed to fetch product ${productId} with related:`, err);
    return null;
  }
}

/**
 * Products Modal - displays all products with "You Might Like" panel
 */
interface ProductsModalProps {
  open: boolean;
  onClose: () => void;
  categoryName: string;
  storeId: string | null;
}

function ProductsModal({ open, onClose, categoryName, storeId }: ProductsModalProps): React.ReactElement {
  const { draft } = useHomeLayout();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // "You Might Like" state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    if (open && categoryName) {
      setLoading(true);
      setSearchTerm('');
      setSelectedProduct(null);
      setRelatedProducts([]);

      const npConfig = draft?.categoryConfig?.newPopularConfig;
      const npName = npConfig?.name || 'New & Popular';
      const isNewPopular = categoryName === npName;
      const fetchFn = isNewPopular
        ? () => fetchPreviewProducts(
            { name: npName, homeFilterType: npConfig?.homeFilterType || 'newOrPopular', homeFilterValue: npConfig?.homeFilterValue },
            storeId, 100
          )
        : () => fetchCategoryProducts(categoryName, storeId, 100);

      fetchFn()
        .then(setProducts)
        .finally(() => setLoading(false));
    }
  }, [open, categoryName, storeId]);

  // Fetch related products when a product is selected
  const handleProductClick = useCallback(async (product: Product) => {
    // Toggle off if clicking the same product
    if (selectedProduct?.id === product.id) {
      setSelectedProduct(null);
      setRelatedProducts([]);
      return;
    }

    setSelectedProduct(product);
    setLoadingRelated(true);

    const fullProduct = await fetchProductWithRelated(product.id, storeId);
    if (fullProduct?.relatedProducts) {
      setRelatedProducts(fullProduct.relatedProducts);
    } else {
      setRelatedProducts([]);
    }
    setLoadingRelated(false);
  }, [selectedProduct, storeId]);

  // Filter products by search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter((p) =>
      p.name.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term) ||
      p.barcode?.toLowerCase().includes(term) ||
      getBrandName(p)?.toLowerCase().includes(term) ||
      p.tags?.some((t) => getTagName(t).toLowerCase().includes(term))
    );
  }, [products, searchTerm]);

  const getProductImage = (product: Product): string | undefined => {
    return product.attachments?.[0]?.thumbnailUrl
      || product.attachments?.[0]?.downloadUrl
      || product.attachments?.[0]?.url;
  };

  const getRelatedProductImage = (related: RelatedProduct['related']): string | undefined => {
    return related.attachments?.[0]?.thumbnailUrl
      || related.attachments?.[0]?.downloadUrl
      || related.attachments?.[0]?.url;
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return 'N/A';
    const numVal = Number(price);
    const safeVal = Number.isFinite(numVal) ? numVal : 0;
    return `R ${safeVal.toFixed(2)}`;
  };

  // Whether to show the "You Might Like" panel
  const showRelatedPanel = selectedProduct !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Fade in={open}>
        <Paper
          sx={{
            width: '90%',
            maxWidth: showRelatedPanel ? 1400 : 1200,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            transition: 'max-width 0.3s ease',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #FF002C 0%, #CC0023 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InventoryIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {categoryName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {loading ? 'Loading...' : `${filteredProducts.length} products`}
                  {storeId && ' • Store filtered'}
                  {selectedProduct && ` • Viewing suggestions for "${selectedProduct.name}"`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, SKU, barcode, brand, or tags... Click a product to see 'You Might Like'"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Main Content - Split View */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Products Table */}
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#FF002C' }} />
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <InventoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography color="text.secondary">
                    {searchTerm ? 'No products match your search' : 'No products in this category'}
                  </Typography>
                </Box>
              ) : (
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Stock</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 100 }}>Suggestions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.map((product) => {
                      const isSelected = selectedProduct?.id === product.id;
                      return (
                        <TableRow
                          key={product.id}
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255, 0, 44, 0.08)',
                              '&:hover': { bgcolor: 'rgba(255, 0, 44, 0.12)' },
                            },
                          }}
                          onClick={() => handleProductClick(product)}
                        >
                          {/* Product Name & Image */}
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                variant="rounded"
                                src={getProductImage(product)}
                                sx={{ width: 48, height: 48, bgcolor: 'grey.100', flexShrink: 0 }}
                              >
                                <ImageIcon sx={{ color: 'grey.400' }} />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {product.name}
                                </Typography>
                                {getBrandName(product) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {getBrandName(product)}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* SKU */}
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {product.sku || '-'}
                            </Typography>
                          </TableCell>

                          {/* Price */}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF002C' }}>
                              {formatPrice(product.priceTier1 || product.price)}
                            </Typography>
                          </TableCell>

                          {/* Stock */}
                          <TableCell>
                            <Chip
                              size="small"
                              label={product.stock}
                              sx={{
                                bgcolor: product.stock > 10 ? '#22C55E' : product.stock > 0 ? '#F97316' : '#EF4444',
                                color: '#FFFFFF',
                                fontWeight: 700,
                                minWidth: 40,
                              }}
                            />
                          </TableCell>

                          {/* View Suggestions Button */}
                          <TableCell>
                            <Chip
                              icon={<YouMightLikeIcon sx={{ fontSize: '16px !important' }} />}
                              label={isSelected ? 'Hide' : 'View'}
                              size="small"
                              variant={isSelected ? 'filled' : 'outlined'}
                              sx={{
                                bgcolor: isSelected ? '#FF002C' : 'transparent',
                                color: isSelected ? '#FFFFFF' : '#FF002C',
                                borderColor: '#FF002C',
                                '& .MuiChip-icon': { color: isSelected ? '#FFFFFF' : '#FF002C' },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            {/* "You Might Like" Panel */}
            {showRelatedPanel && (
              <Box
                sx={{
                  width: 350,
                  borderLeft: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Panel Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <IconButton size="small" onClick={() => { setSelectedProduct(null); setRelatedProducts([]); }}>
                    <BackIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FF002C' }}>
                      You Might Like
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      For: {selectedProduct?.name}
                    </Typography>
                  </Box>
                </Box>

                {/* Related Products Grid */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {loadingRelated ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} sx={{ color: '#FF002C' }} />
                    </Box>
                  ) : relatedProducts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <YouMightLikeIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No suggestions configured
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Configure in Vend
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1.5}>
                      {relatedProducts.map((rp) => (
                        <Grid size={6} key={rp.id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: 1,
                              borderColor: 'grey.200',
                              bgcolor: 'background.paper',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#FF002C',
                                boxShadow: '0 4px 12px rgba(255, 0, 44, 0.1)',
                              },
                            }}
                          >
                            {/* Position badge */}
                            <Chip
                              size="small"
                              label={`#${rp.position + 1}`}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                bgcolor: '#FF002C',
                                color: '#FFFFFF',
                                fontSize: 10,
                                height: 20,
                                zIndex: 1,
                              }}
                            />
                            {/* Product Image */}
                            <Box
                              sx={{
                                width: '100%',
                                aspectRatio: '1',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'grey.100',
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}
                            >
                              {getRelatedProductImage(rp.related) ? (
                                <img
                                  src={getRelatedProductImage(rp.related)}
                                  alt={rp.related.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <ImageIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                              )}
                            </Box>
                            {/* Product Name */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.3,
                                minHeight: 32,
                              }}
                            >
                              {rp.related.name}
                            </Typography>
                            {/* Price & Stock */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#FF002C' }}>
                                {formatPrice(rp.related.priceTier1)}
                              </Typography>
                              <Chip
                                size="small"
                                label={rp.related.stock}
                                sx={{
                                  bgcolor: rp.related.stock > 0 ? '#22C55E' : '#EF4444',
                                  color: '#FFFFFF',
                                  fontSize: 10,
                                  height: 18,
                                }}
                              />
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>

                {/* Panel Footer */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">
                    {relatedProducts.length} suggestion{relatedProducts.length !== 1 ? 's' : ''} •
                    {' '}Synced from Vend + same-category fill
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {filteredProducts.length} products • Click a row to see "You Might Like" suggestions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {storeId ? `Store: ${storeId}` : 'All Stores'}
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}

/**
 * "New & Popular" section with actual products.
 * Uses the configured filter from the draft's newPopularConfig.
 */
interface NewAndPopularPreviewProps {
  storeId: string | null;
  onViewAll?: (categoryName: string) => void;
  /** Number of products to show (default: 4 for mobile, use 8 for web) */
  productLimit?: number;
}

function NewAndPopularPreview({ storeId, onViewAll, productLimit = 6 }: NewAndPopularPreviewProps): React.ReactElement {
  const { draft } = useHomeLayout();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const npConfig = draft?.categoryConfig?.newPopularConfig;
  const npName = npConfig?.name || 'New & Popular';
  const npFilterType = npConfig?.homeFilterType || 'newOrPopular';
  const npFilterValue = npConfig?.homeFilterValue || null;
  const filterLabel = npFilterType === 'newOrPopular' ? 'auto' : npFilterType;

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetchPreviewProducts(
      { name: npName, homeFilterType: npFilterType, homeFilterValue: npFilterValue },
      storeId,
      productLimit
    )
      .then((data) => {
        if (mounted) {
          setProducts(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [storeId, productLimit, npName, npFilterType, npFilterValue]);

  return (
    <Box sx={{ mb: `${SECTION_MARGIN_BOTTOM}px` }}>
      <AppSectionHeader
        title={npName}
        order={1}
        onViewAll={() => onViewAll?.(npName)}
        adornment={
          <Tooltip title="Which filter fills this row" placement="top">
            <Box
              component="span"
              sx={{ fontFamily: APP.font, fontSize: 10, color: APP.grey, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              ({filterLabel})
            </Box>
          </Tooltip>
        }
      />
      <AppRow>
        {loading ? (
          [1, 2, 3, 4].map((i) => <ProductCard key={i} isLoading />)
        ) : products.length > 0 ? (
          products.map((product) => <ProductCard key={product.id} product={product} />)
        ) : (
          // Was four pink tiles with a 🔥 emoji, which looked like a real row of
          // products until you read it. An empty row is empty.
          <EmptyRowNote>Nothing matches this filter at the selected store</EmptyRowNote>
        )}
      </AppRow>
    </Box>
  );
}

/**
 * Brands carousel that fetches and displays actual brands
 * Uses draft brand positions when available, sorted by draft order
 * Filters out brands with no products at selected store (like mobile app)
 */
function BrandsCarousel(): React.ReactElement {
  const { draft } = useHomeLayout();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);

  /*
   * Brands are UNIVERSAL — never scoped to a store.
   *
   * Content Control has no "view as a store" control, so there is no store to
   * scope to and nothing here should try. Two previous attempts did, and both
   * failed the same way, because whatever `selectedStoreId` held was never a
   * store's EXTERNAL id:
   *
   *   - counting products per brand returned 0 for every brand it checked, and
   *     it only checked the first 20 — so the unchecked tail (Uwell, Vaporesso,
   *     VOOPOO, Nasty, AirsPops) took all 8 slots;
   *   - asking the API for `hasProductsAtStore` came back false for every brand,
   *     which emptied the row entirely ("No brands available").
   *
   * Measured against the live API: `?storeId=diep-river` flags 56 of 65 brands
   * available, while a store NAME or any other value flags 0. Anything that
   * reintroduces store scoping here has to prove it is passing an external id.
   *
   * So: fetch every brand and filter on `hasProducts` — "has a sellable product
   * at ANY store", which the API returns unconditionally. That hides brand
   * records with nothing behind them anywhere (ZYN, Vozol, E-XHALE, AirsPops and
   * five others: 9 of 65 on staging) without pretending to know a store. The
   * result is the same list everywhere, and it matches the app's first eight.
   */
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetchBrandsFromApi()
      .then((data) => {
        if (!mounted) return;

        // Fall back to the brand's own live position, exactly as the ordering
        // screen does — otherwise brands missing from the draft all collapse to
        // the same sort key and land in fetch order instead of their real place.
        const draftPositions = draft?.brandPositions?.positions;
        const posMap = new Map(
          (draftPositions ?? []).map((bp: { id: string; position: number }) => [bp.id, bp.position])
        );
        const ordered = [...data].sort(
          (a, b) =>
            (posMap.get(a.id) ?? a.position ?? 999) - (posMap.get(b.id) ?? b.position ?? 999)
        );

        // `!== false` so a backend that hasn't shipped the flag yet shows every
        // brand rather than an empty row.
        const visible = ordered.filter((brand) => brand.hasProducts !== false);

        setBrands(visible.slice(0, BRANDS_VISIBLE));
        setLoading(false);
      })
      .catch(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [draft?.brandPositions]);

  /*
   * BrandSlide: 110pt white cards with the logo contained at 90pt and NO caption —
   * the logo is the label. The row used to be an uppercase "BRANDS" caption over
   * 56pt bordered grey squares with the brand name underneath, which is a data
   * table, not the app's brand rail.
   */
  return (
    <Box sx={{ mb: `${SECTION_MARGIN_BOTTOM}px` }}>
      <AppSectionHeader title="Brands" />
      <AppRow>
        {loading ? (
          [1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: 110,
                height: 110,
                minWidth: 110,
                mr: '12px',
                my: '8px',
                borderRadius: '8px',
                bgcolor: APP.skeletonBase,
                flexShrink: 0,
                animation: 'deeliverSkinPulse 1.5s ease-in-out infinite',
              }}
            />
          ))
        ) : brands.length > 0 ? (
          brands.map((brand) => <AppBrandTile key={brand.id} name={brand.name} imageUrl={brand.imageUrl} />)
        ) : (
          <EmptyRowNote>No brands available</EmptyRowNote>
        )}
      </AppRow>
    </Box>
  );
}

/**
 * Mobile Home Preview - matches actual app layout structure
 * Layout: Banners → New&Popular → First Batch → Carousel → Second Batch → Brands → Bottom Banner
 *
 * Carousel position is read-only here — reordering is done via ConfigurationPanel Home tab.
 */
interface MobileHomePreviewProps {
  /** Banner positions */
  banners: BannerPositionDraft[];
  /** Categories */
  categories: CategoryDraft[];
  /** Selected store ID for product filtering */
  storeId?: string | null;
  /** Callback when View All is clicked */
  onViewAll?: (categoryName: string) => void;
  /** Carousel vertical position configuration */
  carouselVerticalConfig?: CarouselVerticalConfig | null;
  /** Whether to show categories with no products at selected store (greyed out) */
  showEmptyCategories?: boolean;
  /** Change map for compare mode highlighting */
  changeMap?: Map<string, string>;
  /** Anchor key of the section being edited, outlined so it stands out. */
  highlight?: string | null;
}

function MobileHomePreview({
  banners,
  categories,
  storeId = null,
  onViewAll,
  carouselVerticalConfig,
  showEmptyCategories = true,
  changeMap,
  highlight = null,
}: MobileHomePreviewProps): React.ReactElement {
  const topBanner = banners.find((b) => b.name === 'top banner');
  const sliderBanner = banners.find((b) => b.name === 'slider banner');
  const bottomBanner = banners.find((b) => b.name === 'bottom banner');

  // Get active home page categories sorted by scrollerPosition (independent home page order)
  // If showEmptyCategories is false and we have a storeId, filter out categories with no products
  const homeCategories = useMemo(() =>
    categories
      .filter((cat) => cat.isActive && cat.showOnHomePage)
      .filter((cat) => showEmptyCategories || cat.hasProductsAtStore !== false)
      .sort((a, b) => a.scrollerPosition - b.scrollerPosition),
    [categories, showEmptyCategories]
  );

  // Calculate carousel position based on config
  // Auto mode (default): 50% split - carousel after half the scrollers
  // Custom mode: Use the custom position value
  const carouselPosition = useMemo(() => {
    const mode = carouselVerticalConfig?.mode || 'auto';
    if (mode === 'custom' && carouselVerticalConfig?.customPosition) {
      // Custom position is 1-based (after scroller #N)
      // Position 1 = after New & Popular (index 0 in scrollers)
      // So customPosition directly maps to how many scrollers appear before carousel
      return Math.min(carouselVerticalConfig.customPosition, homeCategories.length);
    }
    // Auto: 50% split
    return Math.ceil(homeCategories.length / 2);
  }, [carouselVerticalConfig, homeCategories.length]);

  const firstBatch = homeCategories.slice(0, carouselPosition);
  const secondBatch = homeCategories.slice(carouselPosition);

  /*
   * Static carousel component (read-only — reordering is done via ConfigurationPanel).
   *
   * In the app this is just another CategoryItemsSwiper, titled "Categories" with
   * the same "View All >" as every other row. It used to be drawn as a grey inset
   * panel captioned "CATEGORY ICONS" with a centred "View All Categories →" under
   * it — an editor block sitting in the middle of a phone screen.
   */
  const CarouselComponent = (
    <Box sx={{ mb: `${SECTION_MARGIN_BOTTOM}px` }}>
      <AppSectionHeader title="Categories" />
      <CategoryCarouselPreview categories={categories} />
    </Box>
  );

  /**
   * Outline for the block the open section edits. Data attribute so the panel
   * can scroll to it without threading refs through every child.
   */
  const region = (key: string) => ({
    'data-preview-section': key,
    sx: {
      ...(highlight === key
        ? {
          outline: '2px dashed',
          outlineColor: HIGHLIGHT_COLOR,
          outlineOffset: 2,
          borderRadius: 1,
        }
        : {}),
    },
  });

  return (
    // Always white, never `background.paper`: the app has no dark mode, so a
    // preview that follows the admin theme is showing a screen that can't exist.
    <Box sx={{ bgcolor: APP.white, fontFamily: APP.font, pt: '8px', pb: '8px' }}>
      {/* Top + slider banners. Both belong to the Banners section, so they're
          wrapped together and highlight as one thing. The slider's dots are
          drawn by BannerPreview, below the artwork, as the app draws them. */}
      <Box sx={{ mb: `${SECTION_MARGIN_BOTTOM}px` }}>
        <Box {...region('banners')}>
          <BannerPreview position={topBanner} type="top" />
          <Box sx={{ mt: '12px' }}>
            <BannerPreview position={sliderBanner} type="slider" />
          </Box>
        </Box>
      </Box>

      {/* ========= PRODUCTS FIRST BATCH ========= */}
      <Box {...region('scrollers')}>
        {/* New & Popular - Always first - with actual products */}
        <NewAndPopularPreview storeId={storeId} onViewAll={onViewAll} />

        {/* First batch of category scrollers (before carousel) - with actual products */}
        {firstBatch.map((cat, idx) => (
          <CategoryScroller
            key={cat.id}
            category={cat}
            position={idx + 2}
            storeId={storeId}
            onViewAll={onViewAll}
            changeType={changeMap?.get(cat.id) as CategoryScrollerProps['changeType']}
          />
        ))}
      </Box>

      {/* ========= CATEGORIES CAROUSEL ========= */}
      <Box {...region('carousel')}>{CarouselComponent}</Box>

      {/* ========= PRODUCTS SECOND BATCH ========= */}
      {/* Show ALL remaining categories - no truncation - with actual products */}
      {secondBatch.map((cat, idx) => (
        <CategoryScroller
          key={cat.id}
          category={cat}
          position={idx + firstBatch.length + 2}
          storeId={storeId}
          onViewAll={onViewAll}
          changeType={changeMap?.get(cat.id) as CategoryScrollerProps['changeType']}
        />
      ))}

      {/* ========= BRANDS SECTION ========= */}
      <Box {...region('brands')}>
        <BrandsCarousel />
      </Box>

      {/* Bottom Banner — `isBig`, so 0.5 tall with a 24pt gap under it. */}
      <Box sx={{ mb: '24px' }}>
        <BannerPreview position={bottomBanner} type="bottom" />
      </Box>
    </Box>
  );
}

/**
 * NOT CURRENTLY RENDERED. The preview is phone-only — the Mobile/Web switch was
 * removed. Kept, rather than deleted, because `showInCarouselWeb` is still an
 * editable per-category field (CategoryFormDialog, AllCategoriesTab): the web
 * layout is still configurable, it just has no preview at the moment. Delete
 * this and WebShopPagePreview/BrowserFrame together with that field, not before.
 *
 * Web Home Preview - Full product display with category split logic
 * Same structure as mobile but adapted for web layout (wider, more items)
 */
interface WebHomePreviewProps {
  /** Banner positions */
  banners: BannerPositionDraft[];
  /** Categories */
  categories: CategoryDraft[];
  /** Selected store ID for product filtering */
  storeId?: string | null;
  /** Callback when View All is clicked */
  onViewAll?: (categoryName: string) => void;
  /** Whether to show categories with no products at selected store (greyed out) */
  showEmptyCategories?: boolean;
  /** Change map for compare mode highlighting */
  changeMap?: Map<string, string>;
}

function WebHomePreview({
  banners,
  categories,
  storeId = null,
  onViewAll,
  showEmptyCategories = true,
  changeMap,
}: WebHomePreviewProps): React.ReactElement {
  const topBanner = banners.find((b) => b.name === 'top banner');
  const sliderBanner = banners.find((b) => b.name === 'slider banner');
  const bottomBanner = banners.find((b) => b.name === 'bottom banner');

  // Get active home page categories sorted by scrollerPosition (same logic as mobile)
  // If showEmptyCategories is false and we have a storeId, filter out categories with no products
  const homeCategories = useMemo(() =>
    categories
      .filter((cat) => cat.isActive && cat.showOnHomePage)
      .filter((cat) => showEmptyCategories || cat.hasProductsAtStore !== false)
      .sort((a, b) => a.scrollerPosition - b.scrollerPosition),
    [categories, showEmptyCategories]
  );

  // Split 50/50 matching mobile app logic
  const midPoint = Math.ceil(homeCategories.length / 2);
  const firstBatch = homeCategories.slice(0, midPoint);
  const secondBatch = homeCategories.slice(midPoint);

  // Web view shows more products per scroller (8 instead of 4)
  const WEB_PRODUCT_LIMIT = 8;

  return (
    <Box sx={{ p: 2 }}>
      {/* Top Banner */}
      <Box sx={{ mb: 2 }}>
        <BannerPreview position={topBanner} type="top" />
      </Box>

      {/* Hero Slider */}
      <Box sx={{ mb: 2 }}>
        <BannerPreview position={sliderBanner} type="slider" />
      </Box>

      {/* ========= PRODUCTS FIRST BATCH ========= */}
      {/* New & Popular - Always first */}
      <NewAndPopularPreview storeId={storeId} onViewAll={onViewAll} productLimit={WEB_PRODUCT_LIMIT} />

      {/* First batch of category scrollers */}
      {firstBatch.map((cat, idx) => (
        <CategoryScroller
          key={cat.id}
          category={cat}
          position={idx + 2}
          storeId={storeId}
          onViewAll={onViewAll}
          productLimit={WEB_PRODUCT_LIMIT}
          changeType={changeMap?.get(cat.id) as CategoryScrollerProps['changeType']}
        />
      ))}

      {/* ========= WEB CATEGORIES CAROUSEL ========= */}
      <Box sx={{ my: 2, py: 1.5, bgcolor: 'action.selected', borderRadius: 1, mx: -1, px: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: 10, color: 'text.secondary', letterSpacing: '0.05em' }}
        >
          BROWSE CATEGORIES (WEB)
        </Typography>
        <CategoryCarouselPreview categories={categories} isWeb />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            fontSize: 10,
            color: '#FF002C',
            mt: 0.5,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View All Categories →
        </Typography>
      </Box>

      {/* ========= PRODUCTS SECOND BATCH ========= */}
      {secondBatch.map((cat, idx) => (
        <CategoryScroller
          key={cat.id}
          category={cat}
          position={idx + firstBatch.length + 2}
          storeId={storeId}
          onViewAll={onViewAll}
          productLimit={WEB_PRODUCT_LIMIT}
          changeType={changeMap?.get(cat.id) as CategoryScrollerProps['changeType']}
        />
      ))}

      {/* ========= BRANDS SECTION ========= */}
      <BrandsCarousel />

      {/* Bottom Banner */}
      <Box sx={{ mt: 2 }}>
        <BannerPreview position={bottomBanner} type="bottom" />
      </Box>
    </Box>
  );
}

/**
 * Shop Page Preview - the app's 3-up category grid.
 */
interface ShopPagePreviewProps {
  /** Categories */
  categories: CategoryDraft[];
  /** Whether to show categories with no products at selected store (greyed out) */
  showEmptyCategories?: boolean;
}

/**
 * The Shop tab's category grid, as CategoriesScreen renders it: a 3-up grid of
 * 78pt red tiles (r14, elevated) with the icon filling the tile and a bold
 * centred name under it, on 20pt content padding.
 *
 * It used to be a MUI Grid of full-width rounded squares with a "Shop by
 * Category / N categories" caption, dashed rules down both edges marking a
 * "viewport", an "📱 App viewport (scroll for more)" divider after the 18th
 * tile and a "Show N More" link. The phone frame scrolls, so all of that was
 * simulating something the preview already does for real — and none of it is
 * on the screen being previewed.
 */
function ShopPagePreview({ categories, showEmptyCategories = true }: ShopPagePreviewProps): React.ReactElement {
  // Filter and sort by shopPosition (independent shop page order)
  // If showEmptyCategories is false, filter out categories with no products
  const shopCategories = categories
    .filter((cat) => cat.isActive && cat.showInShop)
    .filter((cat) => showEmptyCategories || cat.hasProductsAtStore !== false)
    .sort((a, b) => a.shopPosition - b.shopPosition);

  if (shopCategories.length === 0) {
    return (
      <Box sx={{ p: '20px' }}>
        <EmptyRowNote>No shop categories</EmptyRowNote>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: APP.white,
        fontFamily: APP.font,
        p: '20px',
        display: 'grid',
        // `numColumns={3}` on mobile. A grid rather than the app's own
        // space-between flex row because that needs invisible padding items to
        // stop a short last row spreading; the columns land in the same place.
        gridTemplateColumns: 'repeat(3, 1fr)',
        justifyItems: 'center',
        rowGap: '20px',
      }}
    >
      {shopCategories.map((cat) => {
        const hasNoProducts = cat.hasProductsAtStore === false;
        return (
          <Tooltip
            key={cat.id}
            title={hasNoProducts ? 'No products at the selected store — hidden in the app' : ''}
            placement="top"
          >
            <Box
              sx={{
                width: 78,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: hasNoProducts ? 0.45 : 1,
              }}
            >
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '14px',
                  bgcolor: APP.red,
                  boxShadow: '0 6px 8px rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {cat.imageUrl ? (
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    style={{ width: 78, height: 78, objectFit: 'contain' }}
                  />
                ) : (
                  <CategoryIcon sx={{ fontSize: 36, color: APP.white }} />
                )}
              </Box>
              <Box
                sx={{
                  mt: '6px',
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: '18px',
                  textAlign: 'center',
                  color: APP.black,
                  width: '100%',
                }}
              >
                {cat.name}
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

/**
 * Web Shop Page Preview - Dynamic columns (4-5 per row) for larger screens
 * Shows categories with showInShop flag in a responsive grid
 */
interface WebShopPagePreviewProps {
  /** Categories */
  categories: CategoryDraft[];
  /** Whether to show all categories (expanded) */
  expanded?: boolean;
  /** Callback when expand toggle is clicked */
  onToggleExpand?: () => void;
  /** Whether to show categories with no products at selected store (greyed out) */
  showEmptyCategories?: boolean;
}

function WebShopPagePreview({ categories, expanded = false, onToggleExpand, showEmptyCategories = true }: WebShopPagePreviewProps): React.ReactElement {
  // Filter and sort by shopPosition (independent shop page order)
  // If showEmptyCategories is false, filter out categories with no products
  const shopCategories = categories
    .filter((cat) => cat.isActive && cat.showInShop)
    .filter((cat) => showEmptyCategories || cat.hasProductsAtStore !== false)
    .sort((a, b) => a.shopPosition - b.shopPosition);

  // Web view: 5 columns * 4 rows = 20 visible items max when collapsed
  const MAX_VISIBLE = 20;
  const hasOverflow = shopCategories.length > MAX_VISIBLE;
  const visibleCategories = expanded ? shopCategories : shopCategories.slice(0, MAX_VISIBLE);
  const hiddenCount = shopCategories.length - MAX_VISIBLE;

  // Web edge padding - larger padding for desktop (40px each side)
  const edgePadding = 40;

  // Web viewport: 5 columns × 2 rows = 10 categories visible without scrolling
  const WEB_VIEWPORT_ITEMS = 10;

  return (
    <Box sx={{ position: 'relative', p: 2 }}>
      {/* Left edge indicator */}
      <Box
        sx={{
          position: 'absolute',
          left: edgePadding,
          top: 0,
          bottom: 0,
          width: 0,
          borderLeft: '2px dashed',
          borderColor: 'divider',
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {/* Right edge indicator */}
      <Box
        sx={{
          position: 'absolute',
          right: edgePadding,
          top: 0,
          bottom: 0,
          width: 0,
          borderRight: '2px dashed',
          borderColor: 'divider',
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary' }}
        >
          Shop by Category
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontSize: 12 }}
        >
          {shopCategories.length} categories
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {visibleCategories.map((cat, index) => {
          const hasNoProducts = cat.hasProductsAtStore === false;
          // Insert viewport divider after 10th item (2 rows × 5 cols for web)
          const showViewportDivider = index === WEB_VIEWPORT_ITEMS && visibleCategories.length > WEB_VIEWPORT_ITEMS;
          return (
            <React.Fragment key={cat.id}>
              {showViewportDivider && (
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      my: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, borderBottom: '2px dashed', borderColor: 'secondary.main', opacity: 0.6 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'secondary.main',
                        fontWeight: 600,
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                        px: 1,
                      }}
                    >
                      🖥️ Web viewport (scroll for more)
                    </Typography>
                    <Box sx={{ flex: 1, borderBottom: '2px dashed', borderColor: 'secondary.main', opacity: 0.6 }} />
                  </Box>
                </Grid>
              )}
              <Grid size={2.4}> {/* size={2.4} = 5 columns (12/2.4=5) */}
                <Tooltip
                  title={hasNoProducts ? 'No products at selected store - will be hidden in app' : ''}
                  placement="top"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      ...(hasNoProducts && {
                        opacity: 0.5,
                      }),
                    }}
                  >
                    {/* Warning badge for empty categories */}
                    {hasNoProducts && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: 4,
                          zIndex: 1,
                          bgcolor: 'warning.main',
                          borderRadius: '50%',
                          width: 16,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 'bold', color: 'white' }}>!</Typography>
                      </Box>
                    )}
                    {/* Square icon */}
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '1',
                        bgcolor: '#FF002C',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        mb: 1,
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        ...(hasNoProducts && {
                          border: '2px dashed',
                          borderColor: 'warning.main',
                        }),
                      }}
                    >
                      {cat.imageUrl ? (
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <CategoryIcon sx={{ fontSize: 40, color: 'white' }} />
                      )}
                    </Box>
                    {/* Category name below icon */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'text.primary',
                        textAlign: 'center',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat.name}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            </React.Fragment>
          );
        })}
        {shopCategories.length === 0 && (
          <Grid size={12}>
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: 14,
              }}
            >
              No shop categories configured
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Expand/Collapse button when there's overflow */}
      {hasOverflow && onToggleExpand && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#FF002C',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={onToggleExpand}
          >
            {expanded ? 'Show Less ↑' : `Show ${hiddenCount} More ↓`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Phone frame wrapper for mobile preview - LARGER for better visibility
 * Supports expanded mode to show all content without scrolling
 */
interface PhoneFrameProps {
  /** Content to render inside the phone */
  children: React.ReactNode;
  /** Label for the preview */
  label?: string;
  /** Store name for the NextDelivery pill in the red header, if a store is picked. */
  storeLabel?: string | null;
  /** Which bottom tab reads as selected. */
  activeTab?: string;
  /** Whether to expand to show all content */
  expanded?: boolean;
  /** Callback when expand toggle is clicked */
  onToggleExpand?: () => void;
}

function PhoneFrame({ children, label, storeLabel, activeTab = 'Home', expanded = false, onToggleExpand }: PhoneFrameProps): React.ReactElement {
  useAppSkinAssets();

  // The frame is laid out at a real phone width and then scaled to whatever
  // room the preview column has. Scaling rather than re-flowing keeps every
  // inner measurement (card widths, icon sizes, the 8px type) in proportion —
  // the preview is meant to be a picture of the app, so it should not relayout
  // itself into something the app would never render.
  const outerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [frameHeight, setFrameHeight] = useState(PHONE_HEIGHT);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) {
        setScale(Math.min(PHONE_MAX_SCALE, Math.max(PHONE_MIN_SCALE, width / FRAME_WIDTH)));
      }
    });
    observer.observe(outer);
    return () => observer.disconnect();
  }, []);

  // offsetHeight, not the observed rect — the frame is transformed, and we need
  // its untransformed layout height to size the space it occupies.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => setFrameHeight(frame.offsetHeight));
    observer.observe(frame);
    setFrameHeight(frame.offsetHeight);
    return () => observer.disconnect();
  }, [expanded]);

  return (
    <Box ref={outerRef} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* Only the compare-mode Live/Draft labels remain here. The expand/collapse
          control is gone: the screen scrolls, so "expand to show all content" was a
          second way to do what scrolling already does, and stretching the frame to an
          arbitrary height stopped it depicting a phone. */}
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
            }}
          >
            {label}
          </Typography>
        </Box>
      )}
      {/* Reserves the scaled footprint — a transform doesn't affect layout, so
          without this the frame would overlap whatever follows it. */}
      <Box sx={{ width: FRAME_WIDTH * scale, height: frameHeight * scale }}>
      <Box
        ref={frameRef}
        data-phone-frame={label || 'draft'}
        sx={{
          // content-box + PHONE_WIDTH, so the outer device measures exactly
          // FRAME_WIDTH — which is what the footprint Box above reserves.
          width: PHONE_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          background: PHONE_CHASSIS,
          border: `1px solid ${PHONE_CHASSIS_EDGE}`,
          borderRadius: `${PHONE_SCREEN_RADIUS + PHONE_BEZEL}px`,
          p: `${PHONE_BEZEL}px`,
          boxShadow: '0 18px 48px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.18)',
          boxSizing: 'content-box',
        }}
      >
        {/*
          The screen. Everything above the scroller is the app's FIXED chrome, in
          the order home.navigator.tsx / HomeScreen render it: status bar over the
          red header (the red runs up behind it, `paddingTop: insets.top + 10`),
          the D monogram + NextDelivery pill + hamburger, the "Deliver to:" bar,
          then the search field. Only what follows scrolls.
        */}
        <Box
          sx={{
            position: 'relative',
            width: PHONE_WIDTH,
            height: expanded ? 'auto' : PHONE_HEIGHT,
            minHeight: expanded ? PHONE_HEIGHT : undefined,
            borderRadius: `${PHONE_SCREEN_RADIUS}px`,
            overflow: 'hidden',
            bgcolor: APP.white,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppStatusBar />
          <AppHeaderBar storeLabel={storeLabel} />
          <AppDeliverToBar />
          <AppSearchField />

          {/* Scroller — the only part of the screen that moves. */}
          <Box
            sx={{
              flex: expanded ? 'none' : 1,
              bgcolor: APP.white,
              overflowY: expanded ? 'visible' : 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 },
            }}
          >
            {children}
          </Box>

          <AppTabBar active={activeTab} />

          {/* Home indicator, on the app's white — not on the chassis. */}
          <Box
            sx={{
              height: 22,
              bgcolor: APP.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box sx={{ width: 134, height: 5, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.85)' }} />
          </Box>

          {/* Notch, cut INTO the screen over the red header rather than drawn as a
              pale block sitting above it. */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 164,
              height: 30,
              bgcolor: '#0B0B0C',
              borderRadius: '0 0 18px 18px',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Box>
      </Box>
    </Box>
  );
}

/**
 * Browser frame wrapper for web preview - looks like a desktop browser
 * Supports expanded mode to show all content without scrolling
 */
interface BrowserFrameProps {
  /** Content to render inside the browser */
  children: React.ReactNode;
  /** Label for the preview */
  label?: string;
  /** Whether to expand to show all content */
  expanded?: boolean;
  /** Callback when expand toggle is clicked */
  onToggleExpand?: () => void;
}

function BrowserFrame({ children, label, expanded = false, onToggleExpand }: BrowserFrameProps): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {label && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
            }}
          >
            {label}
          </Typography>
        )}
        {onToggleExpand && (
          <Tooltip title={expanded ? 'Collapse to default size' : 'Expand to show all content'}>
            <IconButton size="small" onClick={onToggleExpand} sx={{ p: 0.5 }}>
              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          height: expanded ? 'auto' : 600,
          minHeight: expanded ? 600 : undefined,
          maxHeight: expanded ? 'none' : 600,
          bgcolor: '#374151',
          borderRadius: 2,
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Browser toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: '#1F2937',
          }}
        >
          {/* Traffic lights */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#EF4444' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F59E0B' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10B981' }} />
          </Box>
          {/* URL bar */}
          <Box
            sx={{
              flex: 1,
              bgcolor: '#374151',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              ml: 2,
            }}
          >
            <Typography sx={{ fontSize: 11, color: 'grey.400' }}>
              deeliver.co.za
            </Typography>
          </Box>
        </Box>
        {/* Browser content - scrollable or expanded - uses theme background for dark mode */}
        <Box
          sx={{
            flex: expanded ? 'none' : 1,
            bgcolor: 'background.paper', // Theme-aware background
            overflow: expanded ? 'visible' : 'auto',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'grey.100',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 4,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Preview Panel Component
 *
 * Main preview panel with view mode toggle, store selector,
 * and visual previews of the home layout.
 */
export interface PreviewPanelProps {
  /**
   * The Content Control section currently open, or null on the section hub.
   * The preview follows it: Shop switches to the shop view, everything else to
   * the home screen, and the block being edited is outlined and scrolled to.
   */
  section?: ConfigTab | null;
  /**
   * Whether categories with no products at the selected store still appear
   * (greyed out, in position). The switch itself lives on the left, next to the
   * section you're editing — see HomeLayoutManager/index.tsx.
   */
  showEmptyCategories?: boolean;
}

export default React.memo(function PreviewPanel({
  section = null,
  showEmptyCategories = true,
}: PreviewPanelProps = {}): React.ReactElement {
  const {
    draft,
    liveState,
    isLoading,
    selectedStoreId,
    compareMode,
    changes,
  } = useHomeLayout();

  const { isStoreVisible } = useStoreVisibility();
  const [allStores, setAllStores] = useState<Store[]>([]);
  const stores = useMemo(() => allStores.filter(isStoreVisible), [allStores, isStoreVisible]);
  const [filteredPreview, setFilteredPreview] = useState<PreviewData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Which app screen the phone shows. Derived from the open section rather than
  // chosen: the Home/Shop switch was redundant once the preview started following
  // the section you're editing, and could only ever be set to disagree with it.
  const subView: 'home' | 'shop' = section === 'shop' ? 'shop' : 'home';

  /** Block to outline in the phone — null on the hub and on the Shop view. */
  const highlight = section ? SECTION_ANCHORS[section] ?? null : null;

  const previewRootRef = useRef<HTMLDivElement>(null);

  // Bring the relevant block into view. Deferred a frame because the
  // sub-view above may only have just rendered the element we're looking for.
  useEffect(() => {
    if (!highlight) return undefined;

    const id = window.setTimeout(() => {
      previewRootRef.current
        ?.querySelector(`[data-preview-section="${highlight}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);

    return () => window.clearTimeout(id);
  }, [highlight]);

  // Separate expand states for Live and Draft previews (compare mode)
  const [expandedLivePreview, setExpandedLivePreview] = useState(false);
  const [expandedDraftPreview, setExpandedDraftPreview] = useState(false);

  // Products Modal state
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [selectedCategoryForModal, setSelectedCategoryForModal] = useState<string>('');

  // Debounce store switching to prevent wasted requests when clicking rapidly
  const [debouncedStoreId, setDebouncedStoreId] = useState(selectedStoreId);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStoreId(selectedStoreId), 300);
    return () => clearTimeout(timer);
  }, [selectedStoreId]);

  // Change highlighting maps for compare mode
  const draftChangeMap = useMemo(() => {
    if (!compareMode || !changes) return undefined;
    const map = new Map<string, 'added' | 'modified'>();
    for (const item of [...(changes.categories || []), ...(changes.banners || [])]) {
      if (item.type === 'added') map.set(item.id, 'added');
      else if (item.type === 'modified') map.set(item.id, 'modified');
    }
    return map.size > 0 ? map : undefined;
  }, [compareMode, changes]);

  const liveChangeMap = useMemo(() => {
    if (!compareMode || !changes) return undefined;
    const map = new Map<string, 'removed'>();
    for (const item of [...(changes.categories || []), ...(changes.banners || [])]) {
      if (item.type === 'removed') map.set(item.id, 'removed');
    }
    return map.size > 0 ? map : undefined;
  }, [compareMode, changes]);

  // Toggle expand/collapse for live preview
  const toggleExpandLivePreview = useCallback(() => {
    setExpandedLivePreview((prev) => !prev);
  }, []);

  // Toggle expand/collapse for draft preview
  const toggleExpandDraftPreview = useCallback(() => {
    setExpandedDraftPreview((prev) => !prev);
  }, []);

  // Handle View All click - opens products modal
  const handleViewAll = useCallback((categoryName: string) => {
    setSelectedCategoryForModal(categoryName);
    setProductsModalOpen(true);
  }, []);

  // Close products modal
  const handleCloseProductsModal = useCallback(() => {
    setProductsModalOpen(false);
    setSelectedCategoryForModal('');
  }, []);

  // Fetch stores on mount (for store name display)
  useEffect(() => {
    fetchStoresFromApi().then(setAllStores);
  }, []);

  // Fetch filtered preview when store or draft changes
  // This ensures the preview reflects the latest draft after reordering/editing
  // Uses debouncedStoreId to prevent wasted requests when clicking rapidly through stores
  // The view is always 'mobile' now — this panel is the phone preview only.
  useEffect(() => {
    if (debouncedStoreId) {
      setLoadingPreview(true);
      fetchPreviewData(debouncedStoreId, 'mobile')
        .then(setFilteredPreview)
        .finally(() => setLoadingPreview(false));
    } else {
      setFilteredPreview(null);
    }
    // Include draft configs to refetch preview when categories/banners are modified
  }, [debouncedStoreId, draft?.categoryConfig?.categories, draft?.bannerConfig?.positions]);

  // State for categories enriched with hasProductsAtStore
  const [enrichedCategories, setEnrichedCategories] = useState<CategoryDraft[]>([]);

  // Enrich categories with product availability for the selected store
  // Uses fetchPreviewProductsBatch with concurrency=4 to prevent rate limiting
  useEffect(() => {
    const categories = draft?.categoryConfig?.categories || [];
    if (categories.length === 0) {
      setEnrichedCategories([]);
      return;
    }

    // Find store externalId for the products API
    const selectedStore = debouncedStoreId ? stores.find((s) => s.id === debouncedStoreId) : null;
    const storeExternalId = selectedStore?.externalId || null;

    let cancelled = false;
    const checkAvailability = async () => {
      const batchResults = await fetchPreviewProductsBatch(categories, storeExternalId, { limit: 1, concurrency: 4 });
      if (cancelled) return;
      setEnrichedCategories(
        categories.map((cat) => ({
          ...cat,
          hasProductsAtStore: (batchResults.get(cat.id)?.length ?? 0) > 0,
        }))
      );
    };

    checkAvailability();
    return () => { cancelled = true; };
  }, [draft?.categoryConfig?.categories, debouncedStoreId, stores]);

  // State for live categories enriched with hasProductsAtStore
  const [enrichedLiveCategories, setEnrichedLiveCategories] = useState<CategoryDraft[]>([]);

  // Enrich live categories with product availability for the selected store
  useEffect(() => {
    const categories = liveState?.categoryConfig?.categories || [];
    if (categories.length === 0) {
      setEnrichedLiveCategories([]);
      return;
    }

    // Find store externalId for the products API
    const selectedStore = debouncedStoreId ? stores.find((s) => s.id === debouncedStoreId) : null;
    const storeExternalId = selectedStore?.externalId || null;

    let cancelled = false;
    const checkAvailability = async () => {
      const batchResults = await fetchPreviewProductsBatch(categories, storeExternalId, { limit: 1, concurrency: 4 });
      if (cancelled) return;
      setEnrichedLiveCategories(
        categories.map((cat) => ({
          ...cat,
          hasProductsAtStore: (batchResults.get(cat.id)?.length ?? 0) > 0,
        }))
      );
    };

    checkAvailability();
    return () => { cancelled = true; };
  }, [liveState?.categoryConfig?.categories, debouncedStoreId, stores]);

  // Get data for preview - use filtered data if store selected, otherwise draft
  const banners = filteredPreview?.banners ?? draft?.bannerConfig?.positions ?? [];
  // Use enriched categories (with hasProductsAtStore) when available for showEmptyCategories toggle
  const rawCategories = filteredPreview?.categories ?? draft?.categoryConfig?.categories ?? [];
  const categories = enrichedCategories.length > 0 ? enrichedCategories : rawCategories;
  const liveBanners = liveState?.bannerConfig?.positions ?? [];
  // Use enriched live categories (with hasProductsAtStore) when available for showEmptyCategories toggle
  const rawLiveCategories = liveState?.categoryConfig?.categories ?? [];
  const liveCategories = enrichedLiveCategories.length > 0 ? enrichedLiveCategories : rawLiveCategories;

  // Get selected store info for display and product filtering
  // IMPORTANT: Products API expects externalId, not internal id
  const selectedStore = selectedStoreId ? stores.find(s => s.id === selectedStoreId) : null;
  const selectedStoreName = selectedStore?.name || null;
  /**
   * What the NextDelivery pill in the red header reads.
   *
   * The app shows `<location> | OPEN`, where location is the store name minus the
   * "DEELIVER - " prefix (`storeName.split(' - ').slice(1)`). Trading hours aren't
   * available here, so it always reads OPEN — a preview of a shut store isn't a
   * thing Content Control is used to check.
   */
  const storePillLabel = selectedStoreName
    ? `${selectedStoreName.split(' - ').slice(1).join(' - ') || selectedStoreName} | OPEN`
    : null;
  const selectedStoreExternalId = selectedStore?.externalId || null;

  // Loading state
  if (isLoading && !draft) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Skeleton variant="rounded" height={36} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 4 }} />
      </Paper>
    );
  }

  // Show loading when fetching filtered preview
  const showPreviewLoading = loadingPreview && selectedStoreId;

  return (
    <Paper ref={previewRootRef} sx={{ p: 2, height: '100%' }}>
      {/* No chip and no label above the phone.
          The Mobile/Web and Home/Shop switches went first; the "Preview" chip has now
          followed them. The panel is self-evidently the preview — a phone sitting next
          to the editor — and the store picker above already says which store it is, so
          the chip was a label for something nobody was confused about. A guest-mode
          preview is still distinguishable: no store selected means no store data. */}

      {/* Loading indicator for store preview */}
      {showPreviewLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography color="text.secondary">Loading preview for {selectedStoreName}...</Typography>
        </Box>
      )}

      {/* Preview Content */}
      {!showPreviewLoading && compareMode ? (
        // Side-by-side comparison
        <Grid container spacing={2}>
          <Grid size={6}>
            <PhoneFrame label="Live" storeLabel={storePillLabel} activeTab={subView === 'shop' ? 'Shop' : 'Home'} expanded={expandedLivePreview} onToggleExpand={toggleExpandLivePreview}>
              {subView === 'home' ? (
                <MobileHomePreview banners={liveBanners} categories={liveCategories} storeId={selectedStoreExternalId} onViewAll={handleViewAll} carouselVerticalConfig={liveState?.carouselVerticalConfig} showEmptyCategories={showEmptyCategories} changeMap={liveChangeMap} />
              ) : (
                <ShopPagePreview categories={liveCategories} showEmptyCategories={showEmptyCategories} />
              )}
            </PhoneFrame>
          </Grid>
          <Grid size={6}>
            <PhoneFrame label="Draft" storeLabel={storePillLabel} activeTab={subView === 'shop' ? 'Shop' : 'Home'} expanded={expandedDraftPreview} onToggleExpand={toggleExpandDraftPreview}>
              {subView === 'home' ? (
                <MobileHomePreview banners={banners} categories={categories} storeId={selectedStoreExternalId} onViewAll={handleViewAll} carouselVerticalConfig={draft?.carouselVerticalConfig} showEmptyCategories={showEmptyCategories} changeMap={draftChangeMap} highlight={highlight} />
              ) : (
                <ShopPagePreview categories={categories} showEmptyCategories={showEmptyCategories} />
              )}
            </PhoneFrame>
          </Grid>
        </Grid>
      ) : !showPreviewLoading ? (
        // Single preview
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PhoneFrame storeLabel={storePillLabel} activeTab={subView === 'shop' ? 'Shop' : 'Home'} expanded={expandedDraftPreview} onToggleExpand={toggleExpandDraftPreview}>
            {subView === 'home' ? (
              <MobileHomePreview banners={banners} categories={categories} storeId={selectedStoreExternalId} onViewAll={handleViewAll} carouselVerticalConfig={draft?.carouselVerticalConfig} showEmptyCategories={showEmptyCategories} highlight={highlight} />
            ) : (
              <ShopPagePreview categories={categories} showEmptyCategories={showEmptyCategories} />
            )}
          </PhoneFrame>
        </Box>
      ) : null}

      {/* Products Modal */}
      <ProductsModal
        open={productsModalOpen}
        onClose={handleCloseProductsModal}
        categoryName={selectedCategoryForModal}
        storeId={selectedStoreExternalId}
      />
    </Paper>
  );
});
